import React from 'react';
import {
  makeStyles,
  Theme,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
} from '@material-ui/core';
import useVideoContext from '../../hooks/useVideoContext/useVideoContext';
import { useAppState } from '../../state';
import queryString from 'query-string';

const useStyles = makeStyles((theme: Theme) => ({
  button: {
    position: 'fixed',
    top: '200px',
    right: '20px',
    zIndex: 1000,
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
    padding: '12px 24px',
    borderRadius: '8px',
    fontWeight: 'bold',
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
  },
  dialog: {
    '& .MuiDialog-paper': {
      borderRadius: '12px',
      padding: '16px',
    },
  },
  dialogTitle: {
    color: '#f44336',
    fontWeight: 'bold',
  },
  dialogContent: {
    padding: '16px 0',
  },
  confirmButton: {
    backgroundColor: '#f44336',
    color: 'white',
    '&:hover': {
      backgroundColor: '#d32f2f',
    },
  },
  cancelButton: {
    color: '#666',
  },
}));

interface ForceEndButtonProps {
  isVisible: boolean;
}

export default function ForceEndButton({ isVisible }: ForceEndButtonProps) {
  const classes = useStyles();
  const { room } = useVideoContext();
  const { userType, logoutUrl } = useAppState();
  const [openDialog, setOpenDialog] = React.useState(false);
  const [isLoading, setIsLoading] = React.useState(false);
  const query = queryString.parse(window.location.search);
  const roomName = query.room as string;

  const handleForceEnd = async () => {
    if (!room || !roomName) {
      console.error('Room or room name not available');
      return;
    }

    setIsLoading(true);
    try {
      // サーバーAPIを呼び出して全参加者を強制終了
      const endpoint = process.env.REACT_APP_API_ENDPOINT
        ? `${process.env.REACT_APP_API_ENDPOINT}/api/tw/force-end`
        : '/api/tw/force-end';

      console.log('Force end API endpoint:', endpoint);
      console.log('Force end request body:', { room: roomName });

      const response = await fetch(endpoint, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ room: roomName }),
      });

      console.log('Force end response status:', response.status);
      console.log('Force end response headers:', response.headers);

      if (!response.ok) {
        const errorText = await response.text();
        console.error('Force end error response:', errorText);
        throw new Error(`Failed to force end participants: ${response.status} ${errorText}`);
      }

      const result = await response.json();
      console.log('Force end result:', result);

      // 自分も切断
      room.disconnect();

      // 切断後に適切なページに遷移
      setTimeout(() => {
        if (logoutUrl && userType === 'guest') {
          console.log('Force end - Redirecting guest to:', logoutUrl);
          // guestの場合は元のページに戻る
          let guestLogoutUrl = logoutUrl;
          // logoutUrlが管理画面のトップページの場合は、thankyouページに遷移
          if (logoutUrl === 'https://fujikawa-liver-pro.jp.ngrok.io/') {
            // thankyouページに遷移
            guestLogoutUrl = 'https://fujikawa-liver-pro.jp.ngrok.io/liver-wear/talk/thankyou';
            console.log('Force end - Redirecting to thankyou page:', guestLogoutUrl);
          }
          const withParamLogoutUrl = guestLogoutUrl + '?logout=manual';
          window.location.href = withParamLogoutUrl;
        } else if (logoutUrl && userType === 'provider') {
          console.log('Force end - Redirecting provider to:', logoutUrl);
          window.location.href = logoutUrl;
        }
      }, 1000); // 1秒後に遷移（切断処理の完了を待つ）
    } catch (error) {
      console.error('Error force ending participants:', error);
      // エラーが発生した場合は自分だけ切断
      room.disconnect();

      // エラー時も遷移処理を実行
      setTimeout(() => {
        if (logoutUrl && userType === 'guest') {
          console.log('Force end (error) - Redirecting guest to:', logoutUrl);
          // guestの場合は元のページに戻る
          let guestLogoutUrl = logoutUrl;
          // logoutUrlが管理画面のトップページの場合は、thankyouページに遷移
          if (logoutUrl === 'https://fujikawa-liver-pro.jp.ngrok.io/') {
            // thankyouページに遷移
            guestLogoutUrl = 'https://fujikawa-liver-pro.jp.ngrok.io/liver-wear/talk/thankyou';
            console.log('Force end (error) - Redirecting to thankyou page:', guestLogoutUrl);
          }
          const withParamLogoutUrl = guestLogoutUrl + '?logout=manual';
          window.location.href = withParamLogoutUrl;
        } else if (logoutUrl && userType === 'provider') {
          console.log('Force end (error) - Redirecting provider to:', logoutUrl);
          window.location.href = logoutUrl;
        }
      }, 1000);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClick = () => {
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleConfirm = () => {
    handleForceEnd();
    setOpenDialog(false);
  };

  if (!isVisible) {
    return null;
  }

  return (
    <>
      <Button className={classes.button} onClick={handleClick} variant="contained" disabled={isLoading}>
        {isLoading ? '終了中...' : '強制終了'}
      </Button>

      <Dialog open={openDialog} onClose={handleCloseDialog} className={classes.dialog} maxWidth="sm" fullWidth>
        <DialogTitle className={classes.dialogTitle}>通話を強制終了しますか？</DialogTitle>
        <DialogContent className={classes.dialogContent}>
          <Typography>
            この操作により、全参加者の通話が即座に終了されます。
            <br />
            この操作は取り消すことができません。
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog} className={classes.cancelButton}>
            キャンセル
          </Button>
          <Button onClick={handleConfirm} className={classes.confirmButton} variant="contained" disabled={isLoading}>
            {isLoading ? '終了中...' : '強制終了'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
